import { CarePlan } from '@medplum/fhirtypes'
import { AssessmentType } from 'components/Clinical/Assessments/constants'
import { AcutePeriodFlow } from 'contexts/AssessmentContext'
import { CARE_PLAN_ACUTE_PERIOD_EXTENSION_URL } from 'fhir/CarePlan/constants'
import { getAnswerValuesFromItems } from '../helpers'
import { FuaAssessmentQuestionnaireItem } from '../FuaAssessment/types/questionnaire'
import { ReasonForDocumentationValues } from '../FuaAssessment/constants'

export interface FuaFlowsResult {
  flow: AcutePeriodFlow | null
  isForAcutePeriod: boolean
}

interface GetFuaFlowsParams {
  flow: AcutePeriodFlow | null
  flatAnswers: Record<string, any>
  assessmentType: AssessmentType
  patientCarePlans: CarePlan[] | null
}

/**
 * Maps a reason for documentation to the corresponding AcutePeriodFlow
 */
export function getFlowFromReasonForDocumentation(
  reasonForDocumentation: string
): AcutePeriodFlow | null {
  switch (reasonForDocumentation) {
    case ReasonForDocumentationValues.UPDATE_ACUTE_PERIOD:
      return AcutePeriodFlow.EXISTING_ACUTE_PERIOD
    case ReasonForDocumentationValues.CREATE_ACUTE_PERIOD:
      return AcutePeriodFlow.NEW_ACUTE_PERIOD
    case ReasonForDocumentationValues.CHANGE_IN_CONDITION:
      return AcutePeriodFlow.CHANGE_IN_CONDITION
    case ReasonForDocumentationValues.REAUTHORIZATION:
      return AcutePeriodFlow.REAUTHORIZATION
    case ReasonForDocumentationValues.REVISION_TO_POC:
      return AcutePeriodFlow.REVISION_TO_POC
    default:
      return null
  }
}

export function getFuaFlows({
  flow,
  flatAnswers,
  assessmentType,
  patientCarePlans
}: GetFuaFlowsParams): FuaFlowsResult {
  if (assessmentType !== AssessmentType.FUA) {
    return {
      flow,
      isForAcutePeriod: false
    }
  }

  const [revisableCarePlanRef] = getAnswerValuesFromItems(
    Object.values(flatAnswers),
    FuaAssessmentQuestionnaireItem.ASSESSMENT_REVISABLE_CARE_PLAN
  )

  const relatedCarePlan = patientCarePlans?.find((carePlan) => {
    return `CarePlan/${carePlan.id}` === revisableCarePlanRef?.reference
  })

  const isAcutePeriodCarePlan = relatedCarePlan?.extension?.some(
    (extension) => extension.url === CARE_PLAN_ACUTE_PERIOD_EXTENSION_URL
  )

  const isAcutePeriodFlow =
    flow && [AcutePeriodFlow.NEW_ACUTE_PERIOD, AcutePeriodFlow.EXISTING_ACUTE_PERIOD].includes(flow)

  const isRevisionOfAcutePoc = flow === AcutePeriodFlow.REVISION_TO_POC && isAcutePeriodCarePlan
  const isForAcutePeriod = Boolean(isAcutePeriodFlow || isRevisionOfAcutePoc)

  return {
    flow,
    isForAcutePeriod
  }
}
